import { useState } from 'react'
import { Alert, App, Button, Input, Modal, Skeleton } from 'antd'
import dayjs from 'dayjs'
import { Copy } from 'lucide-react'
import type { CustomerChatBindingGrant } from '@super-mega/server/contracts/customer-chat'
import { getErrorMessage } from '@/utils/error.ts'

export function CustomerWecomBindingModal({
  customer,
  grant,
  loading,
  error,
  onGenerate,
  onClose
}: {
  customer: { id: number; name: string }
  grant?: CustomerChatBindingGrant
  loading: boolean
  error: unknown
  onGenerate: () => void
  onClose: () => void
}) {
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const currentGrant = grant?.customerId === customer.id ? grant : undefined
  const prompt = currentGrant
    ? `请将当前企业微信群绑定到 Super Mega 的以下客户：
客户 ID：${currentGrant.customerId}
客户名称：${JSON.stringify(currentGrant.customerName)}

请通过 Hermes 接入的“我的 MCP 服务”执行 bind_current_wecom_group，使用下方一次性绑定码。群 ID 必须来自当前企微群的真实消息上下文，以绑定码限定的客户为准，不要猜测或使用正文中的群 ID。
若存在其他客户或群绑定冲突，请停止，不要覆盖。只有 MCP 返回成功后才告知绑定完成。

SUPER_MEGA_BIND:${currentGrant.token}
有效期至：${dayjs(currentGrant.expiresAt).format('YYYY-MM-DD HH:mm:ss')}`
    : ''

  const copyPrompt = async () => {
    if (!currentGrant || !dayjs(currentGrant.expiresAt).isAfter(dayjs())) {
      void message.warning('绑定码已过期，请重新生成提示词')
      return
    }
    try {
      await navigator.clipboard.writeText(prompt)
      void message.success('提示词已复制，请到目标企微群中 @机器人并粘贴发送')
    } catch {
      void message.error('复制失败，请手动选中下方提示词复制')
    }
  }

  return (
    <Modal
      open={open}
      title="绑定企微群"
      width="42rem"
      destroyOnHidden
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      footer={[
        <Button key="close" onClick={() => setOpen(false)}>
          关闭
        </Button>,
        <Button key="generate" loading={loading} onClick={onGenerate}>
          重新生成
        </Button>,
        <Button
          key="copy"
          type="primary"
          disabled={loading || !currentGrant}
          icon={<Copy aria-hidden size={16} />}
          onClick={() => void copyPrompt()}
        >
          复制提示词
        </Button>
      ]}
    >
      <div className="flex flex-col gap-4">
        <div className="border-border bg-surface-muted rounded-lg border p-3">
          <div className="text-content font-medium break-words">{customer.name}</div>
          <div className="text-content-muted mt-1 text-sm">客户 ID：{customer.id}</div>
        </div>
        <p className="text-content-muted m-0 text-sm leading-6">
          复制下方提示词，进入需要绑定的企业微信群，手动 @机器人后粘贴发送。请确认机器人已加入该群。
        </p>
        {loading ? <Skeleton active paragraph={{ rows: 5 }} /> : null}
        {error ? <Alert type="error" showIcon title={getErrorMessage(error, '绑定提示词生成失败，请重试')} /> : null}
        {currentGrant && !loading ? (
          <Input.TextArea
            aria-label="绑定企微群提示词"
            readOnly
            value={prompt}
            autoSize={{ minRows: 10, maxRows: 16 }}
          />
        ) : null}
        {currentGrant?.currentChatId ? (
          <Alert
            type="warning"
            showIcon
            title="该客户已有关联群"
            description={`当前群 ID：${currentGrant.currentChatId}。再次发送到同一群会返回已绑定；换群请先在客户档案中解除原绑定。`}
          />
        ) : null}
        <Alert
          showIcon
          type="info"
          title="绑定以机器人返回结果为准"
          description="绑定码 10 分钟内有效，仅用于当前客户。请只发送到目标群；重新生成后，本账号为该客户生成的旧码立即失效。完成后可在客户详情核对群 ID。"
        />
      </div>
    </Modal>
  )
}
